// /app/frontend/src/pages/compliance/DataFlowMapPage.jsx
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, Download, Filter, Layers, ZoomIn, ZoomOut, 
  Maximize2, List, Grid3X3, Save, Import, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

// Custom components
import EntityNode from '../../components/compliance/map/EntityNode';
import DataFlowEdge from '../../components/compliance/map/DataFlowEdge';
import NodeDetailsSidebar from '../../components/compliance/map/NodeDetailsSidebar';
import AddNodeModal from '../../components/compliance/map/AddNodeModal';
import ImportFromROPAModal from '../../components/compliance/map/ImportFromROPAModal';
import FilterPanel from '../../components/compliance/map/FilterPanel';
import ExportPanel from '../../components/compliance/map/ExportPanel';
import { initialNodes, initialEdges } from '../../components/compliance/map/initialElements';

// API
import { dataflowMapAPI, entitiesAPI } from '../../services/api';

// Register custom node and edge types
const nodeTypes = { entityNode: EntityNode };
const edgeTypes = { dataFlowEdge: DataFlowEdge };

const defaultEdgeOptions = {
  type: 'dataFlowEdge',
  markerEnd: { type: MarkerType.ArrowClosed, color: '#475569' },
};

const DEFAULT_FILTERS = { nodeTypes: [], riskLevels: [], dataCategories: [], entityId: '' };

// ─── Inner canvas component (uses useReactFlow) ───────────────────────────────
function FlowCanvas({
  nodes, edges, onNodesChange, onEdgesChange, onConnect,
  onNodeClick, onPaneClick, filteredNodes, filteredEdges,
  selectedNode, closeSidebar, deleteNode, openEditModal,
  flowWrapperRef,
}) {
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  return (
    <>
      <div ref={flowWrapperRef} className="flex-1 h-full">
        <ReactFlow
          nodes={filteredNodes}
          edges={filteredEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          className="bg-slate-950"
        >
          <Background variant="dots" gap={20} size={1} color="#1e293b" className="bg-slate-950" />
          <Controls
            className="!bg-slate-900/80 !border-slate-800 !rounded-lg !shadow-xl"
            showInteractive={false}
          />
          <MiniMap
            nodeColor={(node) => {
              const colors = {
                subjects: '#10b981', source: '#3b82f6', process: '#a855f7',
                store: '#f97316', thirdparty: '#06b6d4', transfer: '#f43f5e',
              };
              return colors[node.data?.type] || '#64748b';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
            className="!bg-slate-900/80 !border-slate-800 !rounded-lg"
            pannable
            zoomable
          />
        </ReactFlow>
      </div>

      {/* Floating Toolbar */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-2 rounded-full bg-slate-900/90 backdrop-blur-xl border border-slate-800 shadow-2xl">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => zoomOut()} title="Zoom out">
          <ZoomOut className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => zoomIn()} title="Zoom in">
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => fitView({ padding: 0.2 })} title="Fit view">
          <Maximize2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Node Details Sidebar */}
      <div className={cn(
        "absolute top-0 right-0 h-full transform transition-transform duration-300 ease-in-out z-20",
        selectedNode ? "translate-x-0" : "translate-x-full"
      )}>
        <NodeDetailsSidebar
          node={selectedNode}
          onClose={closeSidebar}
          onDelete={deleteNode}
          onEdit={openEditModal}
        />
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
function DataFlowMapPageInner() {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesState] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [viewMode, setViewMode] = useState('canvas');
  const [entities, setEntities] = useState([]);

  // Modal / Panel open states
  const [showAddNode, setShowAddNode] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [editingNode, setEditingNode] = useState(null);

  // Filter state
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Save state
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Ref for export
  const flowWrapperRef = useRef(null);

  // ── Load saved map on mount ──────────────────────────────────────────────
  useEffect(() => {
    const loadMap = async () => {
      try {
        const res = await dataflowMapAPI.get();
        if (res.data?.exists && res.data.nodes?.length > 0) {
          setNodes(res.data.nodes);
          setEdges(res.data.edges || []);
        }
      } catch {
        // Fall back to initial sample data — no toast needed
      } finally {
        setLoading(false);
      }
    };
    loadMap();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch entities for modals ────────────────────────────────────────────
  useEffect(() => {
    entitiesAPI.getAll().then(res => setEntities(res.data)).catch(() => {});
  }, []);

  // ── Edge connect ─────────────────────────────────────────────────────────
  const onConnect = useCallback((params) => {
    setEdges(eds => addEdge({
      ...params,
      type: 'dataFlowEdge',
      markerEnd: { type: MarkerType.ArrowClosed, color: '#475569' },
      data: { label: 'Data Flow' },
    }, eds));
    toast.success('Connection created');
  }, [setEdges]);

  // ── Node interactions ────────────────────────────────────────────────────
  const onNodeClick = useCallback((_, node) => setSelectedNode(node), []);
  const onPaneClick = useCallback(() => setSelectedNode(null), []);
  const closeSidebar = useCallback(() => setSelectedNode(null), []);

  const deleteNode = useCallback((node) => {
    setNodes(nds => nds.filter(n => n.id !== node.id));
    setEdges(eds => eds.filter(e => e.source !== node.id && e.target !== node.id));
    setSelectedNode(null);
    toast.success('Node deleted');
  }, [setNodes, setEdges]);

  // ── Add Node ─────────────────────────────────────────────────────────────
  const handleAddNode = useCallback((nodeData) => {
    setNodes(nds => [...nds, nodeData]);
    toast.success(`Node "${nodeData.data.label}" added`);
  }, [setNodes]);

  // ── Edit Node ────────────────────────────────────────────────────────────
  const openEditModal = useCallback((node) => {
    setEditingNode(node);
    setShowAddNode(true);
  }, []);

  const handleEditNode = useCallback((updatedNode) => {
    setNodes(nds => nds.map(n => n.id === updatedNode.id ? updatedNode : n));
    setSelectedNode(updatedNode);
    toast.success('Node updated');
  }, [setNodes]);

  // ── Import from ROPA ─────────────────────────────────────────────────────
  const handleImport = useCallback((newNodes, newEdges) => {
    setNodes(nds => [...nds, ...newNodes]);
    setEdges(eds => [...eds, ...newEdges]);
  }, [setNodes, setEdges]);

  // ── Save Map ─────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await dataflowMapAPI.save({ nodes, edges });
      toast.success('Data flow map saved');
    } catch {
      toast.error('Failed to save map');
    } finally {
      setSaving(false);
    }
  }, [nodes, edges]);

  // ── Filters ──────────────────────────────────────────────────────────────
  const activeFilterCount =
    (filters.nodeTypes?.length || 0) +
    (filters.riskLevels?.length || 0) +
    (filters.dataCategories?.length || 0) +
    (filters.entityId ? 1 : 0);

  const filteredNodes = useMemo(() => {
    if (!activeFilterCount) return nodes;
    return nodes.filter(node => {
      if (filters.nodeTypes.length > 0 && !filters.nodeTypes.includes(node.data.type)) return false;
      if (filters.riskLevels.length > 0 && !filters.riskLevels.includes(node.data.risk)) return false;
      if (filters.dataCategories.length > 0) {
        const cats = node.data.dataCategories || [];
        if (!filters.dataCategories.some(c => cats.includes(c))) return false;
      }
      if (filters.entityId && node.data.entityId !== filters.entityId) return false;
      return true;
    });
  }, [nodes, filters, activeFilterCount]);

  const filteredEdges = useMemo(() => {
    if (!activeFilterCount) return edges;
    const visibleIds = new Set(filteredNodes.map(n => n.id));
    return edges.filter(e => visibleIds.has(e.source) && visibleIds.has(e.target));
  }, [edges, filteredNodes, activeFilterCount]);

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    totalNodes: nodes.length,
    totalEdges: edges.length,
    highRiskNodes: nodes.filter(n => n.data.risk === 'high').length,
    transferNodes: nodes.filter(n => n.data.type === 'transfer').length,
  }), [nodes, edges]);

  // Existing node IDs for ImportFromROPA duplicate detection
  const existingNodeIds = useMemo(() => nodes.map(n => n.id), [nodes]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-violet-400 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="data-flow-map-page">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-950/50 backdrop-blur-xl z-20">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-xl font-bold text-white">Data Flow Map</h1>
              <p className="text-sm text-slate-400">Visualize how personal data flows through your organization</p>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Badge variant="outline" className="bg-slate-800/50">{stats.totalNodes} nodes</Badge>
              <Badge variant="outline" className="bg-slate-800/50">{stats.totalEdges} connections</Badge>
              {stats.highRiskNodes > 0 && (
                <Badge variant="outline" className="bg-rose-500/10 text-rose-400 border-rose-500/20">
                  {stats.highRiskNodes} high risk
                </Badge>
              )}
              {stats.transferNodes > 0 && (
                <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20">
                  {stats.transferNodes} transfers
                </Badge>
              )}
              {activeFilterCount > 0 && (
                <Badge variant="outline" className="bg-violet-500/10 text-violet-400 border-violet-500/20">
                  {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
                </Badge>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* View Toggle */}
            <div className="flex items-center bg-slate-800/50 rounded-lg p-1 mr-2">
              <Button
                variant="ghost" size="sm"
                onClick={() => setViewMode('canvas')}
                className={cn("px-3 py-1.5 h-auto", viewMode === 'canvas' && "bg-slate-700 text-white")}
              >
                <Grid3X3 className="h-4 w-4 mr-1" /> Canvas
              </Button>
              <Button
                variant="ghost" size="sm"
                onClick={() => setViewMode('list')}
                className={cn("px-3 py-1.5 h-auto", viewMode === 'list' && "bg-slate-700 text-white")}
              >
                <List className="h-4 w-4 mr-1" /> List
              </Button>
            </div>

            <Button variant="outline" size="sm" onClick={() => setShowFilter(true)}>
              <Filter className="h-4 w-4 mr-2" />
              Filter
              {activeFilterCount > 0 && (
                <Badge className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs bg-violet-600">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
              <Import className="h-4 w-4 mr-2" /> Import ROPA
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowExport(true)}>
              <Download className="h-4 w-4 mr-2" /> Export
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
            <Button
              size="sm"
              className="bg-violet-600 hover:bg-violet-700"
              onClick={() => { setEditingNode(null); setShowAddNode(true); }}
            >
              <Plus className="h-4 w-4 mr-2" /> Add Node
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex relative overflow-hidden">
          {viewMode === 'canvas' ? (
            <FlowCanvas
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesState}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              filteredNodes={filteredNodes}
              filteredEdges={filteredEdges}
              selectedNode={selectedNode}
              closeSidebar={closeSidebar}
              deleteNode={deleteNode}
              openEditModal={openEditModal}
              flowWrapperRef={flowWrapperRef}
            />
          ) : (
            /* List View */
            <div className="flex-1 p-6 overflow-auto">
              <div className="grid grid-cols-1 gap-4">
                {filteredNodes.map((node) => (
                  <div
                    key={node.id}
                    onClick={() => setSelectedNode(node)}
                    className={cn(
                      "p-4 rounded-lg border cursor-pointer transition-all",
                      "bg-slate-900/50 border-slate-800 hover:border-violet-500/50",
                      selectedNode?.id === node.id && "ring-2 ring-violet-500"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <Badge variant="outline" className="mb-1 text-xs capitalize">{node.data.type}</Badge>
                        <h3 className="font-semibold text-white">{node.data.label}</h3>
                        <p className="text-sm text-slate-400 mt-1">{node.data.description}</p>
                      </div>
                      <Badge
                        variant="outline"
                        className={cn(
                          "capitalize",
                          node.data.risk === 'high' && "bg-rose-500/10 text-rose-400 border-rose-500/20",
                          node.data.risk === 'medium' && "bg-amber-500/10 text-amber-400 border-amber-500/20",
                          node.data.risk === 'low' && "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                        )}
                      >
                        {node.data.risk} risk
                      </Badge>
                    </div>
                    {node.data.dataCategories?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {node.data.dataCategories.slice(0, 5).map((cat, i) => (
                          <Badge key={i} variant="outline" className="text-xs bg-slate-800/50">{cat}</Badge>
                        ))}
                        {node.data.dataCategories.length > 5 && (
                          <Badge variant="outline" className="text-xs bg-slate-800/50">
                            +{node.data.dataCategories.length - 5} more
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {filteredNodes.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Layers className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No nodes match the current filters</p>
                  </div>
                )}
              </div>
              {selectedNode && (
                <div className="fixed top-0 right-0 h-full z-50">
                  <NodeDetailsSidebar
                    node={selectedNode}
                    onClose={closeSidebar}
                    onDelete={deleteNode}
                    onEdit={openEditModal}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals & Panels */}
      <AddNodeModal
        open={showAddNode}
        onOpenChange={(v) => { setShowAddNode(v); if (!v) setEditingNode(null); }}
        onAddNode={handleAddNode}
        onEditNode={handleEditNode}
        editingNode={editingNode}
        entities={entities}
      />
      <ImportFromROPAModal
        open={showImport}
        onOpenChange={setShowImport}
        onImport={handleImport}
        existingNodeIds={existingNodeIds}
      />
      <FilterPanel
        open={showFilter}
        onOpenChange={setShowFilter}
        filters={filters}
        onFiltersChange={setFilters}
        entities={entities}
        onReset={() => setFilters(DEFAULT_FILTERS)}
      />
      <ExportPanel
        open={showExport}
        onOpenChange={setShowExport}
        nodes={nodes}
        edges={edges}
        flowRef={flowWrapperRef}
      />
    </DashboardLayout>
  );
}

export default function DataFlowMapPage() {
  return (
    <ReactFlowProvider>
      <DataFlowMapPageInner />
    </ReactFlowProvider>
  );
}
